import { useCallback, useMemo } from 'react';

import {
  intersectLines,
  Line2Struct,
  LineSegment2D,
  Point2D,
  segmentMidpoint,
  vec2DistanceSquared,
  vec2DistanceToLineSquared,
  Vec2Struct,
} from '@paragon/geometry-library';
import { assertExhaustiveSwitch, roundToIncrement } from '@paragon/utilities';

import { useAppSelector } from '../../../components/hooks';
import { getGeometricEcosystemForActivePortionOfLayout } from '../../../store/selectors/layoutGeometricEcosystemSelectors';
import { getPointPickingFixedDistance, getPointPickingOtherPoint } from '../../../store/selectors/pickingSelectors';
import { getActualPointSnappingSettings } from '../../../store/selectors/snappingSettingsSelectors';
import { SnappingLevel } from '../../../store/slices/applicationSlice';
import { SnapType } from '../../../three/dimension/SnapVisual';
import { getDistanceSnap } from '../../../three/hooks/useMousePositionWithBasicInsertionSnapping';
import { getActiveCamera } from '../../../three/reactThreeFiberBindings';
import { EntitySnapTargetSource } from '../EntitySnapTargetSource';
import { snapSegmentEndpointToWholeDegreeAngle } from './angleSnapping';
import { POINT_SNAPPING_CONFIGURATION } from './configuration';
import { PointSnappingSettings } from './settings';

export type MousePositionSnapType =
  'intersection' | 'extension' | 'midpoint' | 'endpoint' | 'nearest' | 'projected' | 'grid' | 'distance' | 'angle';

export interface SnapMousePositionResult {
  /**
   * The type of snap that was applied, or null if no snapping was applied.
   */
  type: MousePositionSnapType | null;

  /**
   * The (possibly snapped) point.
   */
  point: Point2D;
}

/**
 * Returns the snap type that should be used when rendering a snap visual for the given snap result, or null to indicate that a snap visual should not be shown.
 */
export function getSnapVisualTypeForSnappedMousePosition(result: SnapMousePositionResult): SnapType | null {
  switch (result.type) {
    case 'intersection':
    case 'extension':
    case 'distance':
    case 'angle':
      return 'Intersection';

    case 'midpoint':
    case 'endpoint':
      return 'Midline';

    case 'nearest':
    case 'projected':
      return 'Vertex';

    case 'grid':
    case null:
      return null;

    default:
      assertExhaustiveSwitch(result.type);
  }
}

const _snapLineA: Line2Struct = { basePoint: { x: 0, y: 0 }, direction: { x: 0, y: 0 } };
const _snapLineB: Line2Struct = { basePoint: { x: 0, y: 0 }, direction: { x: 0, y: 0 } };
const _snapIntersection: Vec2Struct = { x: 0, y: 0 };
const _snapMidpoint: Vec2Struct = { x: 0, y: 0 };

export function snapMousePosition(
  sourcePoint: Point2D,
  settings: PointSnappingSettings,
  targetSource: EntitySnapTargetSource,

  options: {
    entitySnappingOnly?: boolean;
    otherPoint: Point2D | null;
    distanceSnappingLevel: SnappingLevel;
    fixedDistanceFromOtherPoint?: number | null;
  },
): SnapMousePositionResult {
  const bestEntitySnap: {
    current: { type: MousePositionSnapType; x: number; y: number; distanceToSourceSquared: number } | null;
  } = { current: null };

  const thresholdDistance = getSnappingThreshold();
  const thresholdDistanceSquared = thresholdDistance * thresholdDistance;

  const activeTargets = targetSource.getActiveTargetsForSelectionSettings(settings);

  // not all snapping modes can only consider targets within a certain threshold (extension snapping for example), but
  //   many can, and this greatly aids performance in large layouts
  const targetsWithinThreshold = activeTargets.filter(
    (target) => target.distanceFromSquared(sourcePoint) < thresholdDistanceSquared,
  );

  function addEntitySnap(type: MousePositionSnapType, snappedPoint: Vec2Struct | null) {
    if (snappedPoint == null) return;

    const distanceSquared = vec2DistanceSquared(snappedPoint, sourcePoint);
    if (distanceSquared > thresholdDistanceSquared) return;

    if (bestEntitySnap.current == null || distanceSquared < bestEntitySnap.current.distanceToSourceSquared) {
      bestEntitySnap.current = { type, x: snappedPoint.x, y: snappedPoint.y, distanceToSourceSquared: distanceSquared };
    }
  }

  if (settings.entitySnaps.intersections) {
    for (let i = 0; i < targetsWithinThreshold.length; i++) {
      for (let j = i + 1; j < targetsWithinThreshold.length; j++) {
        const targetA = targetsWithinThreshold[i];
        const targetB = targetsWithinThreshold[j];

        const intersection = targetA.intersectWith(targetB);

        addEntitySnap('intersection', intersection);
      }
    }
  }

  if (settings.entitySnaps.extensionIntersections) {
    // If the target line is not within the threshold distance, then neither will any of its intersections. This is done
    // for performance reasons, since computing all intersections is slow.
    const targetsForExtensions = activeTargets.filter((target) => {
      target.getDefiningLineNoAlloc(_snapLineA);
      return vec2DistanceToLineSquared(sourcePoint, _snapLineA) <= thresholdDistanceSquared;
    });

    for (let i = 0; i < targetsForExtensions.length; i++) {
      targetsForExtensions[i].getDefiningLineNoAlloc(_snapLineA);

      for (let j = i + 1; j < targetsForExtensions.length; j++) {
        targetsForExtensions[j].getDefiningLineNoAlloc(_snapLineB);

        if (intersectLines(_snapLineA, _snapLineB, _snapIntersection)) {
          addEntitySnap('extension', _snapIntersection);
        }
      }
    }
  }

  if (settings.entitySnaps.midpoints) {
    for (const target of targetsWithinThreshold) {
      if (target.definition instanceof LineSegment2D) {
        segmentMidpoint(target.definition, _snapMidpoint);
        addEntitySnap('midpoint', _snapMidpoint);
      }
    }
  }

  if (settings.entitySnaps.endpoints) {
    for (const target of targetsWithinThreshold) {
      if (target.definition instanceof LineSegment2D) {
        addEntitySnap('endpoint', target.definition.basePoint);
        addEntitySnap('endpoint', target.definition.endPoint);
      }
    }
  }

  if (settings.entitySnaps.nearestPoint) {
    let nearestPoint = null;
    let nearestDistanceSquared = Infinity;

    for (const target of targetsWithinThreshold) {
      const projectedPoint = target.getProjectedPoint(sourcePoint);
      if (projectedPoint == null) continue;

      const distanceSquared = vec2DistanceSquared(sourcePoint, projectedPoint);
      if (distanceSquared < nearestDistanceSquared) {
        nearestPoint = projectedPoint;
        nearestDistanceSquared = distanceSquared;
      }
    }

    addEntitySnap('nearest', nearestPoint);
  }

  if (options?.otherPoint && settings.entitySnaps.perpendicularProjection) {
    for (const target of targetsWithinThreshold) {
      const projectedPoint = target.getProjectedPoint(options.otherPoint);

      addEntitySnap('projected', projectedPoint);
    }
  }

  if (options.entitySnappingOnly && bestEntitySnap.current == null) {
    return { type: null, point: sourcePoint };
  }

  let snappedPoint = bestEntitySnap.current
    ? new Point2D(bestEntitySnap.current.x, bestEntitySnap.current.y)
    : sourcePoint;

  let xAxisLocked = false;
  let yAxisLocked = false;
  let angleLocked = false;

  let type = bestEntitySnap.current?.type ?? null;

  if (options?.otherPoint && settings.segmentSnaps.orthogonal) {
    const xChange = Math.abs(options.otherPoint.x - snappedPoint.x);
    const yChange = Math.abs(options.otherPoint.y - snappedPoint.y);

    if (xChange < yChange) {
      snappedPoint = snappedPoint.setX(options.otherPoint.x);
      xAxisLocked = true;
    } else {
      snappedPoint = snappedPoint.setY(options.otherPoint.y);
      yAxisLocked = true;
    }
  }

  if (
    options.otherPoint &&
    !options.otherPoint.equals(snappedPoint) &&
    !settings.segmentSnaps.orthogonal &&
    settings.segmentSnaps.angle &&
    bestEntitySnap.current == null // don't angle snap away from an entity point
  ) {
    const angleSnap = snapSegmentEndpointToWholeDegreeAngle(options.otherPoint, snappedPoint);

    // special case: treat 90 degree angle snapping like orthogonal locking so the grid will still apply
    if (angleSnap.snappedAngleDegrees % 90 === 0) {
      if (Math.abs(angleSnap.snappedAngleDegrees) === 90) {
        snappedPoint = snappedPoint.setX(options.otherPoint.x);
        xAxisLocked = true;
      } else {
        snappedPoint = snappedPoint.setY(options.otherPoint.y);
        yAxisLocked = true;
      }
    } else {
      snappedPoint = angleSnap.point;
      angleLocked = true;
      type = 'angle';
    }
  }

  // Fixed distance is a hard constraint, so apply it after direction-producing snaps and before grid snaps.
  if (options.otherPoint && options.fixedDistanceFromOtherPoint != null && !options.otherPoint.equals(snappedPoint)) {
    const segment = new LineSegment2D(options.otherPoint, snappedPoint);

    return {
      type: 'distance',
      point: segment.toLine().getPointAlongLine(options.fixedDistanceFromOtherPoint),
    };
  }

  if (bestEntitySnap.current == null) {
    if (settings.segmentSnaps.lengthIncrement && options.otherPoint && !options.otherPoint.equals(snappedPoint)) {
      const segment = new LineSegment2D(options.otherPoint, snappedPoint);

      const currentDistance = segment.length;
      const snappedDistance = roundToIncrement(currentDistance, getDistanceSnap(options.distanceSnappingLevel));

      type = 'distance';
      snappedPoint = segment.toLine().getPointAlongLine(snappedDistance);
    }

    if (!xAxisLocked && !angleLocked) {
      type = 'grid';

      snappedPoint = snappedPoint.setX(roundToIncrement(snappedPoint.x, POINT_SNAPPING_CONFIGURATION.GridSize));
    }

    if (!yAxisLocked && !angleLocked) {
      type = 'grid';

      snappedPoint = snappedPoint.setY(roundToIncrement(snappedPoint.y, POINT_SNAPPING_CONFIGURATION.GridSize));
    }
  }

  return { type, point: snappedPoint };
}

export function usePointPickingPointSnapper(options?: {
  /** If false, the returned snapper will be inactive and avoid building snap targets for performance. */

  enabled?: boolean;

  /**
   * If set, only entity snaps will be used (any mode that would result in a point in space, such as grid or distance
   *   snapping, will be disabled).
   */
  entitySnappingOnly?: boolean;
}): (point: Point2D) => SnapMousePositionResult {
  const enabled = options?.enabled ?? true;
  const ecosystem = useAppSelector(getGeometricEcosystemForActivePortionOfLayout);
  const targetSource = useMemo(() => (enabled ? new EntitySnapTargetSource(ecosystem) : null), [enabled, ecosystem]);
  const snappingSettings = useAppSelector(getActualPointSnappingSettings);
  const distanceSnappingLevel = useAppSelector((state) => state.application.snappingLevel);
  const otherPointObject = useAppSelector(getPointPickingOtherPoint);
  const fixedDistanceFromOtherPoint = useAppSelector(getPointPickingFixedDistance);
  const otherPoint = useMemo(() => otherPointObject && Point2D.fromObject(otherPointObject), [otherPointObject]);
  const entitySnappingOnly = options?.entitySnappingOnly;

  return useCallback(
    (point: Point2D) => {
      if (snappingSettings == null || targetSource == null) return { type: null, point };
      return snapMousePosition(point, snappingSettings, targetSource, {
        entitySnappingOnly,
        otherPoint,
        distanceSnappingLevel,
        fixedDistanceFromOtherPoint,
      });
    },
    [
      snappingSettings,
      targetSource,
      entitySnappingOnly,
      otherPoint,
      distanceSnappingLevel,
      fixedDistanceFromOtherPoint,
    ],
  );
}

export function getSnappingThreshold() {
  return Math.max(POINT_SNAPPING_CONFIGURATION.SnappingThreshold * getActiveCamera().getAntiZoomFactor(), 1 / 16);
}
